const HEIC_MIME_TYPES = new Set(['image/heic', 'image/heif']);
const HEIC_EXTENSION_REGEX = /\.(heic|heif)$/i;
const VIDEO_EXTENSION_REGEX = /\.(mp4|mov|webm|m4v)$/i;

const buildFileName = (originalName = 'upload.jpg') =>
  originalName.replace(HEIC_EXTENSION_REGEX, '.jpg');

const ensureBlob = (payload) =>
  payload instanceof Blob ? payload : new Blob([payload], { type: 'image/jpeg' });

const shouldConvertHeic = (file) => {
  if (!file) {
    return false;
  }
  const type = file.type?.toLowerCase();
  if (type && HEIC_MIME_TYPES.has(type)) {
    return true;
  }
  const name = file.name?.toLowerCase() || '';
  return HEIC_EXTENSION_REGEX.test(name);
};

const parseErrorMessage = (error) => {
  if (!error) {
    return 'Unable to convert HEIC image.';
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'object' && 'message' in error && error.message) {
    return String(error.message);
  }
  if (typeof error?.toString === 'function') {
    return error.toString();
  }
  try {
    return JSON.stringify(error);
  } catch (_) {
    return 'Unable to convert HEIC image.';
  }
};

const convertWithHeic2Any = async (file) => {
  const { default: heic2any } = await import('heic2any');
  const converted = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.9,
  });

  const blob = ensureBlob(converted);
  const name = buildFileName(file.name);

  return new File([blob], name, {
    type: 'image/jpeg',
    lastModified: file.lastModified,
  });
};

const convertWithHeicDecode = async (file) => {
  const mod = await import('heic-decode');
  const decode = mod.default ?? mod.decode;
  if (typeof decode !== 'function') {
    throw new Error('HEIC decoder is unavailable.');
  }

  const arrayBuffer = await file.arrayBuffer();
  const uint8Buffer = arrayBuffer instanceof Uint8Array ? arrayBuffer : new Uint8Array(arrayBuffer);
  const { width, height, data } = await decode({ buffer: uint8Buffer });

  if (!width || !height || !data) {
    throw new Error('Decoded HEIC image data is incomplete.');
  }

  const canvas = document.createElement('canvas');
  try {
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas 2D context is unavailable.');
    }

    const clamped = data instanceof Uint8ClampedArray ? data : new Uint8ClampedArray(data);
    const imageData = new ImageData(clamped, width, height);
    context.putImageData(imageData, 0, 0);

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (result) => {
          if (result) {
            resolve(result);
          } else {
            reject(new Error('Canvas toBlob returned null.'));
          }
        },
        'image/jpeg',
        0.9
      );
    });

    const name = buildFileName(file.name);
    return new File([blob], name, {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } finally {
    // Best-effort cleanup
    canvas.width = 0;
    canvas.height = 0;
  }
};

const MAX_IMAGE_DIMENSION = 2048;
const JPEG_QUALITY = 0.85;

const loadImageFromFile = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (event) => {
      URL.revokeObjectURL(url);
      reject(event?.error || new Error('Failed to load image for compression.'));
    };
    img.src = url;
  });

const compressImageIfNeeded = async (file) => {
    if (!file || !file.type?.startsWith('image/')) {
      return file;
    }

    try {
      const img = await loadImageFromFile(file);
      const { width, height } = img;
      if (!width || !height) {
        return file;
      }

      const scale = Math.min(MAX_IMAGE_DIMENSION / width, MAX_IMAGE_DIMENSION / height, 1);
      const canvasWidth = Math.max(Math.round(width * scale), 1);
      const canvasHeight = Math.max(Math.round(height * scale), 1);

      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const context = canvas.getContext('2d');
      if (!context) {
        return file;
      }
      context.drawImage(img, 0, 0, canvasWidth, canvasHeight);

      const blob = await new Promise((resolve) =>
        canvas.toBlob(
          resolve,
          'image/jpeg',
          JPEG_QUALITY
        )
      );

      if (!blob || blob.size === 0) {
        return file;
      }

      if (blob.size >= file.size) {
        return file;
      }

      return new File([blob], file.name, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    } catch (error) {
      console.warn('Image compression failed:', error);
      return file;
    }
};

export const normalizeUploadFile = async (file) => {
  if (!shouldConvertHeic(file)) {
    return file.type?.startsWith('image/')
      ? await compressImageIfNeeded(file)
      : file;
  }

  const attempts = [convertWithHeic2Any, convertWithHeicDecode];
  const errors = [];

  for (const attempt of attempts) {
    try {
      const converted = await attempt(file);
      return await compressImageIfNeeded(converted);
    } catch (error) {
      const message = parseErrorMessage(error);
      errors.push(message);
      console.warn(`HEIC conversion attempt failed: ${message}`);
    }
  }

  const combinedMessage = errors.length
    ? errors.join(' | ')
    : 'Unable to convert HEIC image.';
  throw new Error(
    `${combinedMessage}${combinedMessage.endsWith('.') ? '' : '.'} Please export the image as JPEG and try again.`
  );
};

export const normalizeMediaType = (mediaType, mediaUrl) => {
  if (typeof mediaType === 'string' && mediaType.trim()) {
    const normalized = mediaType.trim().toLowerCase();
    if (normalized.startsWith('video')) {
      return 'video';
    }
    if (normalized.startsWith('image')) {
      return 'image';
    }
  }

  if (
    typeof mediaUrl === 'string' &&
    VIDEO_EXTENSION_REGEX.test((mediaUrl.split('?')[0] || '').trim())
  ) {
    return 'video';
  }

  return 'image';
};
